import { IFilterData, IGalleryItem, IInvestment, LatLon } from 'utils/globalTypes';

import { IAddress, IPastTrade, ITableItem, ITradeHistory } from './interfaces';

export type Response<D> = {
  data: D;
};

export type ResponseWithType<T, A> = {
  data: {
    type: T;
    attributes: A;
  };
};

export enum OfferingStatus {
  draft = 0,
  active = 1,
  disabled = 2
}

export enum OfferingType {
  secondary = 0,
  primary = 1,
  promotion = 2
}

export enum VisibilityType {
  default = 0,
  permission = 1,
  hidden = 2,
  public = 3
}

export enum PromotionType {
  none = 0,
  featured = 1,
  suppressed = 10
}

export enum SponsorType {
  sponsor = 0,
  operator = 1,
  syndicator = 2
}

export enum ImportStatus {
  skipped = 0,
  added = 1,
  updated = 2,
  deleted = 3
}

export enum OrderStatus {
  pending = 0,
  active = 1,
  matched = 2,
  executing = 5,
  executed = 10,
  canceled = 100
}

export enum OrderType {
  int_buy = 0,
  bid_buy = 1,
  bid_sell = 2
}

export enum TradeStatus {
  pending = 0,
  executing = 5,
  executed = 10,
  pending_executing = 50,
  pending_executed = 51,
  canceled = 100
}

export enum TransactionType {
  holdings_buy = 0,
  holdings_sell = 1,
  distributions = 2,
  management_fees = 3
}

export enum PermissionType {
  documents = 0,
  offering = 1
}

export enum FeeType {
  offering_buyer_fees = 1,
  offering_seller_fees = 2
}

export enum NotificationStatus {
  unread = 0,
  read = 1,
  archived = 2
}

export enum NotificationType {
  transaction = 0,
  portfolio = 1,
  offering = 2,
  general = 10,
  product = 11,
  support = 12
}

export enum NotificationPriority {
  low = 0,
  normal = 1,
  high = 2
}

export enum DocumentStatus {
  pending = 0,
  done = 1,
  canceled = 2
}

export enum DocumentType {
  docusign = 0,
  resource = 1
}

export interface IListOrdersResponse {
  total?: number;
  data: Array<{ attributes: IOrder }>;
}

export interface IOrderResponse {
  data: { attributes?: IOrder };
}

export interface IOrderAction {
  id: string;
  parent_id: number;
  order_id: number;
  offering_id: string;
  investor_id: string;
  action_type: string;
  action_status: number;
  start_at: number;
  end_at: number;
  document_id?: string;
  transfer_id?: string;
  approval_id?: string;
}

export interface IListOrderActionsResponse {
  data: Array<{ attributes: IOrderAction }>;
}

export interface IOrderActionResponse {
  data: { attributes?: IOrderAction };
}

export interface IBaseItem extends IFilterData {
  id?: string;
  holding_type?: number;
  geo_location?: LatLon;
  media?: string[];
  gallery_media?: IGalleryItem[];
  investor_id?: string;
  sponsor_id?: string;
  offering_price?: number;
  offering_quantity?: number;
  transaction_date?: number;
  order_type?: OrderType;

  [key: string]: unknown;
}

export interface IListBaseItemsResponse {
  data: Array<IBaseItem>;
}

export interface IPermission extends IBaseItem {
  permission_type?: PermissionType;
}

export interface IFee extends IBaseItem {
  fee_type?: FeeType;
  fee_amount?: number;
  fee_comment?: string;
}

export interface ITransaction extends IBaseItem {
  holding_id?: string;
  order_id?: number;
  trade_id?: number;
  transaction_type?: TransactionType;
  transaction_date?: number;
  currency?: string;
  amount?: number;
  deleted_at?: number;
  fees?: number;
  aml_details?: IAmlDetails;
}

export interface IHolding extends IBaseItem {
  holding_date?: number;
  start_quantity?: number;
  start_price?: number;
  start_total?: number;
  quantity?: number;
  price?: number;
  total?: number;
  currency?: string;
  total_dividends_investor?: number;
  t12_yield_investor?: number;
  deleted_at?: number;
}

export interface IListHoldingsResponse {
  data: Array<{ attributes: IHolding }>;
}

export interface IOrder extends IBaseItem {
  order_status?: number;
  order_type?: number;
  promotion_type?: number;

  quantity?: number;
  price?: number;
  total?: number;
  last_buy_price?: number;
  buy_total?: number;
  sell_total?: number;
  capital_gain?: number;
  return_pct?: number;

  currency?: string;
  start_at?: number;
  end_at?: number;
  min_quantity?: number;

  personal_details?: IPersonalDetails;
  financial_details?: IFinancialDetails;
  investor_silent?: boolean;
  document_sign_date?: number;
  investor_approval_id?: string;
  investor_approval_date?: number;
  sponsor_approval_id?: string;
  sponsor_approval_date?: number;
  document_id?: string;
  fees?: number;
  fees_comment?: string;
  total_fees?: number;
}

export interface ITradeResponse {
  data: { attributes?: ITrade };
}

export interface ITrade extends IBaseItem {
  buyer_id?: string;
  buyer_order_id?: string;
  buyer_holding_id?: string;
  buyer_aml_id?: string;
  buyer_aml_details?: string;
  buyer_email?: string;
  buyer_display_name?: string;

  seller_id?: string;
  seller_order_id?: string;
  seller_holding_id?: string;
  seller_aml_id?: string;
  seller_email?: string;
  seller_display_name?: string;

  trade_status?: number;
  quantity?: number;
  price?: number;
  total?: number;
}

export interface IPublishEntity {
  id?: string;
  name?: string;
  creator_id?: string;
  entityType: 'offering' | 'sponsor';

  [key: string]: unknown;
}

export interface IOffering {
  id?: string;
  name?: string;
  offering_id?: string;
  offering_name?: string;
  asset_id?: string;
  type?: string;
  media?: string[];
  sponsor_id?: string;
  creator_id?: string;
  content_key?: string;
  asset_content_key?: string;
  sponsor_content_key?: string;
  offering_status?: number;
  offering_type?: number;
  promotion_type?: number;
  currency?: string;
  acquisition_price?: number;
  acquisition_date?: number;
  recap_price?: number;
  recap_date?: number;
  equity?: number;
  implied_valuation?: number;
  implied_cap_rate?: number;
  quantity?: number;
  total_quantity?: number;
  price?: number;
  buy_price?: number;
  valuation?: number;
  discount?: number;
  t12_noi?: number;
  t12_noi_date?: number;
  t12_distributions?: number;
  t12_yield?: number;
  strategy?: string;
  target_equity?: number;
  target_aum_size?: number;
  investment_types?: string;

  hold_date?: number;
  start_at?: number;
  end_at?: number;
  address?: IAddress;

  marketplace_metrics?: string[];
  offering_promo_metrics?: string[];
  offering_metrics?: string[];

  max_discount?: number;
  max_premium?: number;
  min_holding_amount?: number;
  min_invest_amount?: number;
  max_invest_amount?: number;
  buyer_fees?: number;
  seller_fees?: number;

  buyer_fees_investor?: number;
  buyer_fees_comment_investor?: string;
  seller_fees_investor?: number;
  seller_fees_comment_investor?: string;
  buyer_fees_comment?: string;
  seller_fees_comment?: string;

  require_buy_document?: boolean;
  require_sell_document?: boolean;

  quantity_holding?: number;
  quantity_holding_investor?: number;
  volume_holding_investor?: number;
  total_dividends_investor?: number;

  quantity_executed_buy?: number;
  quantity_executed_int_buy?: number;
  quantity_executed_bid_buy?: number;
  quantity_executed_bid_sell?: number;
  quantity_executed_buy_investor?: number;
  quantity_executed_int_buy_investor?: number;
  quantity_executed_bid_buy_investor?: number;
  quantity_executed_bid_sell_investor?: number;
  quantity_pending_int_buy_?: number;
  quantity_pending_bid_buy?: number;
  quantity_pending_bid_sell?: number;
  quantity_pending_int_buy_investor?: number;
  quantity_pending_bid_buy_investor?: number;
  quantity_pending_bid_sell_investor?: number;

  avg_price_pending_int_buy_?: number;
  avg_price_pending_bid_buy?: number;
  avg_price_pending_bid_sell?: number;
  avg_price_pending_bid_buy_investor?: number;
  avg_price_pending_bid_sell_investor?: number;
  avg_price_executed_buy?: number;
  avg_price_executed_int_buy?: number;
  avg_price_executed_bid_buy?: number;
  avg_price_executed_bid_sell?: number;
  avg_price_holding_investor?: number;
  avg_price_executed_int_buy_investor?: number;
  avg_price_executed_bid_buy_investor?: number;
  avg_price_executed_bid_sell_investor?: number;

  max_price_pending_int_buy?: number;
  max_price_pending_bid_buy?: number;
  min_price_pending_bid_sell?: number;

  pending_int_buy_order_price_investor?: number;
  pending_int_buy_order_quantity_investor?: number;
  pending_int_buy_order_status_investor?: number;

  pending_bid_buy_order_price_investor?: number;
  pending_bid_buy_order_quantity_investor?: number;
  pending_bid_buy_order_status_investor?: number;

  pending_bid_sell_order_price_investor?: number;
  pending_bid_sell_order_quantity_investor?: number;
  pending_bid_sell_order_status_investor?: number;

  active_orders?: IActiveOrders;
  trade_history?: ITradeHistory;
  past_trades?: IPastTrade[];
  watch?: boolean;

  [key: string]: unknown;
}

export interface ISponsor {
  id?: string;
  name?: string;
  content_key?: string;
  creator_id?: string;
  logo?: string;

  [key: string]: unknown;
}

export interface IOfferingResponse {
  data: { attributes?: IOffering };
}

export interface IListOfferingsResponse {
  data: Array<{ attributes: IOffering }>;
}

export interface IListSponsors {
  data: Array<{ attributes: ISponsor }>;
}

export interface IActivity {
  description: string;
  link?: string;
  date: Date;
}

export interface ITotalEarnings {
  dividends?: number;
  shares_sell?: number;
  shares_buy?: number;
  management_fees?: number;
  dividends_fees?: number | null;
  shares_sell_fees?: number | null;
  shares_buy_fees?: number | null;
  holdings?: number | null;
}

export interface IDividends {
  year: number;
  quarter: number;
  amount: number;
}

export interface ISummary {
  currency?: string;
  summary?: ITotalEarnings;
  dividends?: IDividends[];
  activity?: IActivity[];
  counts?: { [key: string]: number };
}

export interface IDocument extends IBaseItem {
  order_id?: string;
  template_id?: string;
  document_id?: string;
  document_status?: number;
  document_type?: number;
  name?: string;
  description?: string;
  document_url?: string;
}

export type IDocumentList = Array<IDocument>;

export interface IListDocumentsResponse {
  total?: number;
  data: Array<{ attributes: IDocument }>;
}

export interface IDocumentResponse {
  data: { attributes?: IDocument };
}

export interface INotification {
  id: string;
  notification_type: number;
  notification_priority?: number;
  created_at: number;
  updated_at: number;
  read_at?: number;
  message?: string;
  offering_id?: string;
  offering_name?: string;
  media?: string[];
  trade_id?: string;
  order_id?: string;
  order_type?: number;
}

export enum DomicileType {
  us_citizen = 'U.S. Citizen',
  us_resident = 'U.S. Resident',
  non_resident = 'Non Resident'
}

// Personal details
export interface IPersonalDetails {
  first_name?: string;
  last_name?: string;
  birth_date?: string;
  zipcode?: string;
  country?: string;
  phone?: string;
  gender?: string;
  domicile?: DomicileType;
  primary_address?: string;
  additional_address?: string;
  city?: string;
  state?: string;

  [key: string]: unknown;
}

export interface IUtmDetails {
  utm_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;

  [key: string]: unknown;
}

// Financial details
export interface IFinancialDetails {
  accredited?: boolean;
  investment_objective?: string[];
  investment_experience?: string[];
  investment_value?: string;
  profession?: string;
  liquidity?: string;
  annual_income?: string;
  want_to_invest?: string;

  [key: string]: unknown;
}

export enum UserStatus {
  active = 0,
  pending = 1,
  deleted = 5,
  blocked = 10
}

export enum UserType {
  investor = 0,
  sponsor = 1,
  admin = 2
}

export interface IUser {
  id?: string;
  email?: string;
  name?: string;
  created_at?: number;
  updated_at?: number;
  user_status?: number;
  user_type?: number;
  display_name?: string;
  creator_id?: string;
  investor_id?: string;
  sponsor_id?: string;
  active_at?: number;
  ip_address?: string;
  country?: string;
  verified?: boolean;
  approved?: boolean;
  pending?: boolean;
  system_unsubscribe?: boolean | number;
  email_unsubscribe?: boolean | number;
  email_mfa_enabled?: boolean | number;
  groups?: string[];
  personal_details?: IPersonalDetails;
  financial_details?: IFinancialDetails;
  invite_link?: string;

  delivery_status?: string;
  delivery_error?: string;

  [key: string]: unknown;
}

export interface IUserResponse {
  data: { attributes?: IUser };
}

export enum AmlType {
  individual = 0,
  entity = 1
}

export interface IAmlDetails {
  aml_type?: number;
  ein?: string;
  entity_name?: string;

  ssn?: string;
  first_name?: string;
  last_name?: string;
  birth_date?: string;

  country?: string;
  state?: string;
  city?: string;
  address?: string;
  address_ext?: string;
  zipcode?: string;

  [key: string]: unknown;
}

export interface IAml extends IAmlDetails {
  id?: string;
  created_at?: number;
  updated_at?: number;

  [key: string]: unknown;
}

export interface IListAmlsResponse {
  total?: number;
  data: Array<{ attributes: IAml }>;
}

export interface IAmlResponse {
  data: { attributes?: IAml };
}

export interface IHoldingResponse {
  data: { attributes?: IHolding };
}

export interface IOrderResponse {
  data: { attributes?: IOrder };
}

export interface ITransactionResponse {
  data: { attributes?: ITransaction };
}

export interface IPermissionResponse {
  data: { attributes?: IPermission };
}

export enum EventStatus {
  draft = 0,
  scheduled = 1,
  sent = 2,
  archived = 3,
  deleted = 99
}

export enum EventType {
  offering_bid_buy = 1,
  offering_bid_sell = 2,
  offering_activity = 3,
  offering_marketing = 4,
  offering_introduction = 5
}

export interface EventTemplate {
  version?: string;
  name?: string;
  source: string;
  notification: string;
  subject?: string;
  main_content?: string;
  text: string;
  html: string;
  config_set: string;
  content_url: string;
}

export interface EmailEvent extends IInvestment {
  created_at?: number;
  updated_at?: number;
  sent_at?: number;
  event_status?: EventStatus;
  event_type?: EventType;
  sender_email?: string;
  sender_name?: string;
  subject?: string;
  main_content?: string;
  scheduled_time?: number;
  template_id?: string;
  target_all?: boolean;
  template_clone?: boolean;
  creator_email?: string;
  creator_name?: string;
  target_emails?: string[];
  amount_targeted?: number;
  amount_sent?: number;
  amount_send?: number;
  amount_delivery?: number;
  amount_open?: number;
  amount_click?: number;
  amount_hard_bounce?: number;
  amount_spam?: number;
  amount_error?: number;
  amount_other?: number;
  amount_open_unique?: number;
  amount_click_unique?: number;
}

export interface EventTemplates {
  [event_type: number]: EventTemplate;
}

export type GetEventTemplateResponse = Response<EventTemplate>;
export type ListEventTemplateResponse = Response<{ [event_type: number]: EventTemplate }>;
export type UpdateEventResponse = ResponseWithType<'Event', EmailEvent>;
export type CreateEventResponse = ResponseWithType<'Event', EmailEvent>;
export type TestEventResponse = ResponseWithType<'Event', EmailEvent>;
export type GetEventResponse = ResponseWithType<'Event', EmailEvent>;

export enum ReportStatus {
  manual = 0,
  recurring = 1,
  archived = 2,
  scheduled = 3,
  deleted = 99
}

export enum ReportType {
  registered_users = 'registered_users',
  registered_users_kyc = 'registered_users_kyc',
  user_permissions = 'user_permissions',
  all_orders = 'all_orders',
  active_orders = 'active_orders',
  executed_orders = 'executed_orders',
  canceled_orders = 'canceled_orders',
  all_trades = 'all_trades',
  active_trades = 'active_trades',
  executed_trades = 'executed_trades',
  canceled_trades = 'canceled_trades'
}

export enum ReportInterval {
  once = 0,
  daily = 1,
  weekly = 2,
  monthly = 3
}

export interface IReport extends ITableItem {
  id?: string;
  created_at?: number;
  updated_at?: number;
  executed_at?: number;
  report_status?: ReportStatus;
  report_type?: string;
  name?: string;
  description?: string;
  sponsor_id?: string;
  schedule_interval?: number;
  target_emails?: string[];
  total?: number;
  base_from_date?: number;
  from_date?: number;
  to_date?: number;
  creator_email?: string;
  creator_name?: string;
  report_path?: string;
}

export interface IActiveOrder {
  price?: number;
  quantity?: number;
  total: number;
  order_type?: OrderType;
  is_self?: boolean;
}

export type IActiveOrders = Array<IActiveOrder>;

export interface IChartSeries {
  [key: string]: number;
}

export interface IChartData {
  [key: string]: IChartSeries;
}

export interface EmailUnsubscribe {
  email?: string;
  token?: string;
  system_unsubscribe?: boolean;
  email_unsubscribe?: boolean;
}
